import { OptionalBlankableStringSchema, isTruthy } from 'notable-common';
import { z } from 'zod';
import { FlowBlockValueEmailSchema } from '../../blockValue/emailSchema.js';
import { BaseFlowBlockSchema } from '../block.js';
import { BaseFlowNotableObjectConfigSchema, FlowNotableObject } from '../notableObject.js';
import { FlowBlockType } from '../type.js';
import { VoiceCallModel } from '../voiceCall/model.js';
export const FlowUpdatePatientOrderConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PatientOrder),
    patientOrderId: z.string(),
    status: z.string().optional(),
    requestedAppointmentTimePeriodStart: z.string().nullish(),
    requestedAppointmentTimePeriodEnd: z.string().nullish(),
    procedures: z
        .array(z
        .object({
        description: z.string(),
        cptCode: z.string().nullish(),
        areaOfInterest: z.string().nullish(),
    })
        .strip())
        .optional(),
    diagnoses: z
        .array(z.object({
        icd10Codes: z.array(z.string()).nullish(),
        description: z.string(),
    }))
        .optional(),
    specialty: z.string().nullish(),
    referredByDepartment: z.string().nullish(),
    referredToDepartment: z.string().nullish(),
    reasonForExam: z.string().nullish(),
    referredByEhrProviderFirstName: z.string().optional(),
    referredByEhrProviderLastName: z.string().optional(),
    referredByEhrProviderValue: z.string().optional(),
    referredByEhrProviderEmail: z.string().optional(),
    referredToEhrProviderFirstName: z.string().optional(),
    referredToEhrProviderLastName: z.string().optional(),
    referredToEhrProviderValue: z.string().optional(),
    referredToEhrProviderEmail: z.string().optional(),
    ehrProviderKind: z.string().optional(),
});
export const FlowUpdatePaymentFeeType = z.enum(['copay']);
export const FlowUpdatePaymentConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Payment),
    encounterId: z.string(),
    feeType: FlowUpdatePaymentFeeType,
    feeAmount: z.string(),
});
export var FlowProviderActivationMode;
(function (FlowProviderActivationMode) {
    FlowProviderActivationMode["UpdateOnly"] = "UpdateOnly";
    FlowProviderActivationMode["Activate"] = "Activate";
})(FlowProviderActivationMode || (FlowProviderActivationMode = {}));
export var FlowPrevisitFormTemplatesMode;
(function (FlowPrevisitFormTemplatesMode) {
    FlowPrevisitFormTemplatesMode["AutoApply"] = "AutoApply";
    FlowPrevisitFormTemplatesMode["ManualApply"] = "ManualApply";
})(FlowPrevisitFormTemplatesMode || (FlowPrevisitFormTemplatesMode = {}));
// This enum must be kept in sync with the FlowExportNoteEvent enum in the primary/src/models/user/preference.ts
export var FlowExportNoteEvent;
(function (FlowExportNoteEvent) {
    FlowExportNoteEvent["Disabled"] = "DISABLED";
    FlowExportNoteEvent["OnPrevisitSubmit"] = "ON_PREVISIT_SUBMIT";
    FlowExportNoteEvent["OnCheckIn"] = "ON_CHECK_IN";
    FlowExportNoteEvent["OnReadyForProvider"] = "ON_READY_FOR_PROVIDER";
})(FlowExportNoteEvent || (FlowExportNoteEvent = {}));
export const FlowUpdateProviderConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Provider),
    ehrId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    npi: OptionalBlankableStringSchema,
    suffix: OptionalBlankableStringSchema,
    email: FlowBlockValueEmailSchema({ isRequired: true }),
    ehrKind: z.string().optional(),
    hl7Id: OptionalBlankableStringSchema,
    apiId: OptionalBlankableStringSchema,
    providerActivationMode: z
        .enum(FlowProviderActivationMode)
        .default(FlowProviderActivationMode.UpdateOnly),
    previsitFormTemplatesMode: z
        .enum(FlowPrevisitFormTemplatesMode)
        .default(FlowPrevisitFormTemplatesMode.ManualApply),
    exportFingerprintNoteMode: z.enum(FlowExportNoteEvent).default(FlowExportNoteEvent.Disabled),
    providerUserId: OptionalBlankableStringSchema,
});
export const FlowUpdateEncounterConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Encounter),
    payload: z.string(),
});
export const FlowUpdatePatientConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Patient),
    payload: z.string(),
});
export const FlowUpdateAssistantConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Assistant),
    assistantId: z.string(),
    model: z.enum(VoiceCallModel),
    prompt: z.string(),
    firstMessage: z.string().optional(),
});
export const FlowUpdatePatientInsuranceInformationConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PatientInsuranceInformation),
    patientInsuranceInformationId: z.string(),
    planName: z.string(),
});
export const FlowUpdatePatientUploadConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PatientUpload),
    patientUploadId: z.string(),
    // Resolved through replaceBlockValues, so these hold templates as often as literals; primary validates the resolved value.
    documentType: z.string().optional(),
    status: z.string().optional(),
    existingDocument: z.string().optional(),
    existingDocumentUploadDate: z.string().nullish(),
});
export const FlowUpdateNotableObjectConfigSchema = z.discriminatedUnion('notableObject', [
    FlowUpdateEncounterConfigSchema,
    FlowUpdatePatientOrderConfigSchema,
    FlowUpdatePaymentConfigSchema,
    FlowUpdateProviderConfigSchema,
    FlowUpdatePatientConfigSchema,
    FlowUpdateAssistantConfigSchema,
    FlowUpdatePatientInsuranceInformationConfigSchema,
    FlowUpdatePatientUploadConfigSchema,
]);
export const FlowUpdateNotableObjectBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.UpdateNotableObject),
    config: FlowUpdateNotableObjectConfigSchema.superRefine((data, context) => {
        if (data.notableObject === FlowNotableObject.Provider) {
            if (data.providerActivationMode === FlowProviderActivationMode.Activate &&
                !isTruthy(data.providerUserId?.trim())) {
                context.addIssue({
                    code: 'custom',
                    message: 'Provider User ID is required when Provider Activation Mode is set to Activate',
                    path: ['providerUserId'],
                });
            }
        }
    }),
});
//# sourceMappingURL=config.js.map